import React, { useEffect, useState } from 'react';

const houseAds = [
  {
    id: 'tokenos',
    title: 'Build AI-Powered Web3 dApps',
    subtitle: 'TokenOS is the AI operating system for Web3. Launch agents, dApps, and payment flows.',
    cta: 'Explore TokenOS',
    url: 'https://tokenos.ai',
  },
  {
    id: 'quake',
    title: 'Shake the Markets with QuakeTrading',
    subtitle: 'Zero-decay leveraged trading on Solana. No holding fees, just amplified power.',
    cta: 'Trade with Quake',
    url: 'https://quake.trading',
  },
  {
    id: 'predictbot',
    title: 'Prediction Markets in Your Pocket',
    subtitle: 'PredictBot brings 500+ prediction markets straight into Telegram.',
    cta: 'Launch PredictBot',
    url: 'https://predictbot.net',
  },
];

const AdBanner = () => {
  const isProduction = process.env.NODE_ENV === 'production';
  const client = process.env.REACT_APP_ADSENSE_PUBLISHER_ID;
  const slot = process.env.REACT_APP_ADSENSE_GAME_SLOT_ID;

  const shouldShowRealAds = Boolean(isProduction && client && slot);

  const [useFallback, setUseFallback] = useState(!shouldShowRealAds);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    // Rotate house ads when fallback is active
    if (!useFallback) return;
    const interval = window.setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % houseAds.length);
    }, 8000);
    return () => window.clearInterval(interval);
  }, [useFallback]);

  useEffect(() => {
    if (!shouldShowRealAds) {
      setUseFallback(true);
      return;
    }

    setUseFallback(false);

    let timeoutId = window.setTimeout(() => {
      // If AdSense script has not successfully initialized by now, fall back
      if (!window.__adsense_loaded) {
        setUseFallback(true);
      }
    }, 4000);

    // Load AdSense script once
    if (!window.__adsense_loaded) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${client}`;
      script.crossOrigin = 'anonymous';
      script.onload = () => {
        window.__adsense_loaded = true;
        window.clearTimeout(timeoutId);
        try {
          // eslint-disable-next-line no-undef
          (window.adsbygoogle = window.adsbygoogle || []).push({});
          setUseFallback(false);
          window.__ad_mode = 'adsense';
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('AdSense init error:', e);
          setUseFallback(true);
          window.__ad_mode = 'fallback';
        }
      };
      script.onerror = () => {
        window.clearTimeout(timeoutId);
        // eslint-disable-next-line no-console
        console.error('Failed to load AdSense script');
        setUseFallback(true);
        window.__ad_mode = 'fallback';
      };
      document.head.appendChild(script);
    } else {
      try {
        // eslint-disable-next-line no-undef
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        window.clearTimeout(timeoutId);
        setUseFallback(false);
        window.__ad_mode = 'adsense';
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('AdSense push error:', e);
        setUseFallback(true);
        window.__ad_mode = 'fallback';
      }
    }

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [shouldShowRealAds, client, slot]);

  if (useFallback) {
    const ad = houseAds[currentIndex] || houseAds[0];
    return (
      <div className="w-full bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-white/10 py-2 px-3 flex items-center justify-center">
        <div className="max-w-4xl w-full flex items-center justify-between gap-3 text-xs md:text-sm">
          <div className="flex flex-col md:flex-row md:items-baseline md:gap-2">
            <span className="text-emerald-300 font-semibold uppercase tracking-wide text-[10px] mb-0.5 md:mb-0">
              Sponsored by GameOS partners
            </span>
            <span className="text-gray-50 font-semibold">{ad.title}</span>
            <span className="hidden md:inline text-gray-300">— {ad.subtitle}</span>
          </div>
          <a
            href={ad.url}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 inline-flex items-center rounded-full bg-emerald-500/90 hover:bg-emerald-400 text-black px-3 py-1 text-[11px] font-semibold transition"
          >
            {ad.cta}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex justify-center bg-black/90 border-b border-white/10 py-2">
      <ins
        className="adsbygoogle"
        style={{ display: 'block', width: '100%', maxWidth: 728, minHeight: 90 }}
        data-ad-client={client}
        data-ad-slot={slot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
};

export default AdBanner;
